'use client';

import { useState } from 'react';
import { conventionCategoryBadge } from '../lib/badgeColors';
import { cn } from '../lib/cn';
import { formatTokenCountLabel } from '../lib/formatNumber';

export type UserConventionOption = {
  id: string;
  title: string;
  category: string;
  contentTokenCount?: number | null;
  runnerInjectedCount: number;
  trigger?: string | null;
  updatedAt?: string | null;
};

type UserConventionSelectorProps = {
  label: string;
  emptyHint: string;
  conventions: UserConventionOption[];
  selectedIds: string[];
  onToggle: (conventionId: string, checked: boolean) => void;
  maxHeightClassName?: string;
};

export const sortUserConventionOptionsByRunnerInjectionOrder = (
  conventions: UserConventionOption[],
): UserConventionOption[] => {
  return [...conventions].sort((left, right) => {
    const runnerInjectedCountDiff = right.runnerInjectedCount - left.runnerInjectedCount;
    if (runnerInjectedCountDiff !== 0) return runnerInjectedCountDiff;

    const leftUpdatedAt = left.updatedAt ? Date.parse(left.updatedAt) : 0;
    const rightUpdatedAt = right.updatedAt ? Date.parse(right.updatedAt) : 0;
    const updatedAtDiff = rightUpdatedAt - leftUpdatedAt;
    if (updatedAtDiff !== 0) return updatedAtDiff;

    return left.id.localeCompare(right.id);
  });
};

export const prepareUserConventionOptionsForPicker = (conventions: UserConventionOption[]): UserConventionOption[] => {
  return sortUserConventionOptionsByRunnerInjectionOrder(
    conventions.filter((convention) => convention.trigger !== 'always_on'),
  );
};

export const UserConventionSelector = ({
  label,
  emptyHint,
  conventions,
  selectedIds,
  onToggle,
  maxHeightClassName = 'max-h-40',
}: UserConventionSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const conventionIdSet = new Set(conventions.map((convention) => convention.id));
  const selectedVisibleIds = selectedIds.filter((id) => conventionIdSet.has(id));
  const selectedTokenCount = conventions.reduce((sum, convention) => {
    if (!selectedIds.includes(convention.id)) return sum;
    return sum + (convention.contentTokenCount ?? 0);
  }, 0);

  return (
    <div className="flex min-w-0 flex-col rounded-lg border border-base-300 bg-base-100">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
      >
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-[var(--color-fg-subtle)]">{label}</span>
        {selectedTokenCount > 0 ? (
          <span className="shrink-0 text-[10px] font-medium text-[var(--color-fg-subtle)]">
            {formatTokenCountLabel(selectedTokenCount)}
          </span>
        ) : null}
        <span className="rounded-full bg-[var(--chip-neutral-bg)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--chip-neutral-fg)]">
          {selectedVisibleIds.length}
        </span>
        <svg
          className={cn('h-4 w-4 shrink-0 text-[var(--color-fg-muted)] transition-transform', isOpen && 'rotate-180')}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {isOpen ? (
        conventions.length === 0 ? (
          <p className="border-t border-base-300 px-3 py-2 text-xs text-[var(--color-fg-muted)]">{emptyHint}</p>
        ) : (
          <div className={cn('overflow-y-auto border-t border-base-300 p-2 space-y-1', maxHeightClassName)}>
            {conventions.map((convention) => (
              <label
                key={convention.id}
                className="flex cursor-pointer items-start gap-2 rounded-md px-1 py-0.5 text-xs hover:bg-base-200/70"
              >
                <input
                  type="checkbox"
                  className="checkbox checkbox-xs mt-0.5"
                  checked={selectedIds.includes(convention.id)}
                  onChange={(event) => onToggle(convention.id, event.target.checked)}
                />
                <span className="flex min-w-0 flex-1 items-center gap-1.5">
                  <span
                    className={cn(
                      'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                      conventionCategoryBadge[convention.category as keyof typeof conventionCategoryBadge] ??
                        'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]',
                    )}
                  >
                    {convention.category}
                  </span>
                  <span className="truncate font-medium">{convention.title}</span>
                </span>
              </label>
            ))}
          </div>
        )
      ) : null}
    </div>
  );
};
